import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Save, Wand2, Copy, Download } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  if (r.headers.get('content-type')?.includes('application/json')) return r.json();
  return r.text();
};

const STI_PA_PROVIDERS = [
  { v: 'iconectiv',    label: 'iConectiv (default STI-PA)' },
  { v: 'transunion',   label: 'TransUnion' },
  { v: 'first-orion',  label: 'First Orion' },
  { v: 'numeracle',    label: 'Numeracle' },
  { v: 'comsolve',     label: 'Comsolve' },
];

export default function StirShaken() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';

  const cfg = useQuery<any>({
    queryKey: ['stir-shaken', active],
    queryFn: () => fJson(`/api/stir-shaken/${active}`),
    enabled: !!active,
  });

  const [form, setForm] = useState<any>({});
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/stir-shaken/${active}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['stir-shaken', active] }); setForm({}); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const genDev = useMutation({
    mutationFn: () => fJson(`/api/stir-shaken/${active}/generate-dev-cert`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['stir-shaken', active] }); toast('success', 'Dev self-signed cert generated (NOT valid for production)'); },
    onError: (e: any) => toast('error', e.message),
  });

  async function copyKamailioSnippet() {
    try {
      const text = await fJson(`/api/stir-shaken/${active}/kamailio-snippet`);
      await navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text));
      toast('success', 'Kamailio snippet copied');
    } catch (e: any) { toast('error', e.message); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><ShieldCheck size={20} /> STIR/SHAKEN</h1>
        <select className="input max-w-xs" value={active} onChange={e => setDomain(e.target.value)}>
          {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>

      <div className="card p-3 border-l-4" style={{ borderLeftColor: 'rgb(34 197 94)' }}>
        <div className="text-sm">
          <b>STIR/SHAKEN signing:</b> The FCC's Robocall Mitigation Database requires call-origin attestation for US carriers. Stratus signs via Kamailio's <code>secsipid</code> module per tenant.
        </div>
      </div>

      <div className="card p-4 grid grid-cols-2 gap-3">
        <Field label="Enabled">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
            Sign outbound INVITEs from this tenant
          </label>
        </Field>
        <Field label="STI-PA provider">
          <select className="input" value={f.sti_pa_provider || ''} onChange={e => set('sti_pa_provider', e.target.value || null)}>
            <option value="">— Choose —</option>
            {STI_PA_PROVIDERS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Service-Provider Code (OCN)">
          <input className="input font-mono" value={f.sp_code || ''} onChange={e => set('sp_code', e.target.value)} placeholder="e.g. 123A" />
        </Field>
        <Field label="Default attestation">
          <select className="input" value={f.default_attestation || 'B'} onChange={e => set('default_attestation', e.target.value)}>
            <option value="A">A — Full attestation (we own the calling number)</option>
            <option value="B">B — Partial attestation (we know the customer but not the number)</option>
            <option value="C">C — Gateway attestation (we just transit)</option>
          </select>
        </Field>
        <Field label="Cert URL (public)">
          <input className="input font-mono" value={f.cert_url || ''} onChange={e => set('cert_url', e.target.value)} placeholder="https://certs.example.com/tenant-a.pem" />
        </Field>
        <Field label=" "><span className="text-xs text-textSecondary">Carriers fetch this URL to verify your Identity header.</span></Field>
        <div className="col-span-2">
          <label className="block text-sm text-textSecondary mb-1">Certificate (PEM)</label>
          <textarea className="input font-mono text-xs" rows={6} value={f.cert_pem || ''} onChange={e => set('cert_pem', e.target.value)} placeholder="-----BEGIN CERTIFICATE-----..." />
        </div>
        <div className="col-span-2">
          <label className="block text-sm text-textSecondary mb-1">Private key (PEM) — write-only, redacted on read</label>
          <textarea className="input font-mono text-xs" rows={6} value={f.private_key_pem || ''} onChange={e => set('private_key_pem', e.target.value)}
            placeholder={f.private_key_pem === '[redacted]' ? '[stored — paste new value to rotate]' : '-----BEGIN PRIVATE KEY-----...'} />
        </div>
        <div className="col-span-2">
          <label className="block text-sm text-textSecondary mb-1">Notes</label>
          <input className="input" value={f.notes || ''} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2 flex justify-between items-center mt-2">
          <div className="flex gap-2">
            <button onClick={() => genDev.mutate()} className="btn-ghost inline-flex items-center gap-2"><Wand2 size={14} /> Generate dev self-signed</button>
            <button onClick={copyKamailioSnippet} className="btn-ghost inline-flex items-center gap-2"><Copy size={14} /> Copy Kamailio snippet</button>
            <a href={`/api/stir-shaken/${active}/kamailio-snippet`} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2"><Download size={14} /> View snippet</a>
          </div>
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
